import { gql } from 'apollo-boost';
import { FRAGMENT_BOOKING_DETAILS_BOOKING_STUDENT_DETAILS_BOOKING } from '~containers/booking/booking-details/gql';

const FRAGMENT_EDIT_INFO = gql`
  fragment fragmentStudentEditInfo on EditInfo {
    createdAt
    createdBy
    updatedAt
    updatedBy
  }
`;

export const FRAGMENT_STUDENT_USER = gql`
  fragment fragmentStudentDetailsStudentUser on StudentUser {
    id
    originId
    email
    emailVerified
    editInfo {
      ...fragmentStudentEditInfo
    }
    studentInfo {
      id
      fullName
      gender
      profilePhotoFileUuid
      generalInfo {
        basic {
          dateOfBirth
          nationality
        }
      }
      bookings: baseBookings(propertyId: $propertyId) {
        id
        status
      }
    }
  }
  ${FRAGMENT_EDIT_INFO}
`;

const FRAGMENT_CONTACT = gql`
  fragment fragmentStudentDetailsContact on Contact {
    id
    firstName
    middleName
    lastName
    title
    dateOfBirth
    contactType
    relationship
    countryTerritoryId
    countryTerritory
    cityTownId
    cityTown
    addressLine1
    addressLine2
    postcode
    mobilePhoneAreaCode
    mobilePhoneNumber
    homePhoneAreaCode
    homePhoneNumber
    contactEmail
    contactWithoutPermission
    editInfo {
      ...fragmentStudentEditInfo
    }
  }
  ${FRAGMENT_EDIT_INFO}
`;

export const FRAGMENT_STUDENT_DETAILS_STUDENT = gql`
  fragment fragmentStudentDetailsStudent on Student {
    account {
      ...fragmentStudentDetailsStudentUser
    }
    bookings: baseBookings(propertyId: $propertyId) {
      ...fragmentBookingDetailsBookingStudentDetailsBooking
    }
    communicationPreferences
    contact {
      emergencyContacts {
        ...fragmentStudentDetailsContact
      }
    }
    editInfo {
      ...fragmentStudentEditInfo
    }
    customFieldValues {
      id
      labelName
      value
      customFieldId
      customFieldType
      originId
    }
    email
    externalId
    firstName
    fullName
    gender
    preferredLanguage
    preferredLanguageFull
    preferredGenderPronoun
    generalInfo {
      basic {
        dateOfBirth
        nationality
        title
      }
      contact {
        addressLine1
        addressLine2
        cityTown
        cityTownId
        contactEmail
        countryTerritory
        countryTerritoryId
        homePhoneAreaCode
        homePhoneNumber
        mobilePhoneAreaCode
        mobilePhoneNumber
        postcode
      }
      education {
        degreeClassification
        destinationUniversity
        major
        universityCardId
        yearOfStudy
      }
    }
    heardSource
    id
    isRebooker
    lastName
    middleName
    originId
    preferredName
    profileCompletion {
      emailVerified
      emergencyContacts
      generalInfo
      profilePhoto
      totalPoint
    }
    profilePhotoFileUuid
    isAnonymized
  }
  ${FRAGMENT_CONTACT}
  ${FRAGMENT_STUDENT_USER}
  ${FRAGMENT_EDIT_INFO}
  ${FRAGMENT_BOOKING_DETAILS_BOOKING_STUDENT_DETAILS_BOOKING}
`;

export const QUERY_STUDENT_BY_ID = gql`
  query QueryStudentUerById($propertyId: ID!, $id: ID!) {
    getStudent(propertyId: $propertyId, id: $id) {
      ...fragmentStudentDetailsStudent
    }
  }
  ${FRAGMENT_STUDENT_DETAILS_STUDENT}
`;

export const QUERY_STUDENT_BY_EMAIL = gql`
  query QueryStudentUerByEmail($propertyId: ID!, $email: String!) {
    getStudentUserByEmail(propertyId: $propertyId, email: $email) {
      id
      studentInfo {
        id
        fullName
        email
      }
    }
  }
`;

export const QUERY_COUNTRIES = gql`
  query QueryCountries($nameLike: String) {
    getCountries(nameLike: $nameLike) {
      countries {
        id
        name
      }
    }
  }
`;

export const QUERY_NATIONALITY = gql`
  query QueryNationality($nameLike: String) {
    getCountries(nameLike: $nameLike) {
      countries {
        id
        nationality
      }
    }
  }
`;

export const QUERY_CITIES = gql`
  query QueryCities($nameLike: String, $countryId: ID) {
    getCities(nameLike: $nameLike, countryId: $countryId) {
      cities {
        id
        name
      }
    }
  }
`;

export const QUERY_UNIVERSITIES = gql`
  query QueryUniversities($cityId: ID, $nameLike: String) {
    getUniversities(cityId: $cityId, nameLike: $nameLike) {
      universities {
        id
        name
      }
    }
  }
`;

export const MUTATION_SEND_RESET_PASSWORD_EMAIL = gql`
  mutation TriggerStudentForgotPassword($propertyId: ID!, $id: ID!) {
    triggerStudentForgotPassword(propertyId: $propertyId, id: $id) {
      success
    }
  }
`;

export const MUTATION_CREATE_STUDENT_CONTACT = gql`
  mutation CreateStudentContact($propertyId: ID!, $input: createStudentContactInput!) {
    createStudentContact(propertyId: $propertyId, input: $input) {
      id
    }
  }
`;

export const MUTATION_UPDATE_STUDENT_CONTACT = gql`
  mutation UpdateStudentContact($propertyId: ID!, $input: updateStudentContactInput!) {
    updateStudentContact(propertyId: $propertyId, input: $input) {
      ...fragmentStudentDetailsContact
    }
  }
  ${FRAGMENT_CONTACT}
`;

export const MUTATION_DELETE_STUDENT_CONTACT = gql`
  mutation DeleteStudentContact($propertyId: ID!, $id: ID!) {
    deleteStudentContact(propertyId: $propertyId, id: $id) {
      deletedId
    }
  }
`;

export const MUTATION_UPDATE_STUDENT_PROFILE_PHOTO = gql`
  mutation UpdateStudentProfilePhoto($propertyId: ID!, $input: updateStudentProfilePhotoInput!) {
    updateStudentProfilePhoto(propertyId: $propertyId, input: $input) {
      ...fragmentStudentDetailsStudent
    }
  }
  ${FRAGMENT_STUDENT_DETAILS_STUDENT}
`;

export const MUTATION_UPDATE_STUDENT = gql`
  mutation UpdateStudent($propertyId: ID!, $input: updateStudentInput!) {
    updateStudent(propertyId: $propertyId, input: $input) {
      ...fragmentStudentDetailsStudent
    }
  }
  ${FRAGMENT_STUDENT_DETAILS_STUDENT}
`;

export const UPDATE_STUDENT_EMAIL = gql`
  mutation UpdateStudentUserEmail($propertyId: ID!, $studentUser: updateStudentUserEmailInput!) {
    updateStudentUserEmail(propertyId: $propertyId, studentUser: $studentUser) {
      ...fragmentStudentDetailsStudentUser
    }
  }
  ${FRAGMENT_STUDENT_USER}
`;

export const QUERY_CHECK_CAN_ERASURE_STUDENT = gql`
  query CheckCanErasureStudent($propertyId: ID!, $input: erasureStudentInput!) {
    checkCanErasureStudent(propertyId: $propertyId, input: $input) {
      success
    }
  }
`;
